<script setup>
import { defineProps, onMounted, ref, defineEmits, watch, computed } from "vue";
import { useCustomer } from '@/hooks/shopify-hooks/useCustomer';
import PersonForm from "./PersonForm.vue";
import BirthFrom from "./BirthFrom.vue";
import AddressForm from "./AddressForm.vue";
import PasswordForm from "./PasswordForm.vue";
import { useTipMsgBox } from "@/hooks/common/msg-box/useTipMsgBox";
import { isPhonePortrait } from '@/hooks/common/useUtils';
import { useLogOut } from "@/hooks/shopify-hooks/useShopifyMessageEvent";
import { _CSR_IS_B2b } from '@/const/shopifyConst.js';
import { useGetStorage } from '@/hooks/common/useStorage';
import { shopifyConst } from '@/hooks/shopify-hooks/useShopifyConst.js';
import { usePhoneAreaCodes } from '@/hooks/common/usePhoneAreaCodes';
const { c4aOAuth2State} = shopifyConst;
const c4aOAuth2StateNew = c4aOAuth2State === 'true' ? true : false;

const props = defineProps({
  stateKey: {
    type: String,
    default: "",
  },
  config: {
    type: Object,
    default: null
  },
  customer: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['update']);
const acceptsMarketing = ref(false);
const showPersonForm = ref(false);
const showBirthFrom = ref(false);
const showAddressForm = ref(false);
const showPasswordForm = ref(false);
const showLogout = ref(false); // 显示logout按钮
const currentAddress = ref({}); // 当前编辑的地址
const isB2b = ref(false);
const loading = ref(false);

// isB2b.value = localStorage.getItem(_CSR_IS_B2b) === 'true';
isB2b.value = useGetStorage(_CSR_IS_B2b) === true;

// 区号来自线上配置（brand[site]），用于手机号脱敏时匹配区号前缀
const { areaCodes, load: loadAreaCodes } = usePhoneAreaCodes();

// 手机号脱敏：只保留区号与后四位，中间用 * 代替，如 +86 ********5678
const maskedPhone = computed(() => {
  const full = String(props.customer?.phone || '').trim();
  if (!full) {
    return
  };
  // 按区号长度降序匹配，避免 +8 误匹配 +84 这类前缀包含的情况
  const codes = [...areaCodes.value].sort((a, b) => b.length - a.length);
  const matched = codes.find((code) => code && full.startsWith(code));
  const areaCode = matched || '';
  const rest = full.slice(areaCode.length);
  // 号码太短（不足4位）时全部打码，避免泄露
  if (rest.length <= 4) {
    return `${areaCode} ${'*'.repeat(rest.length)}`.trim();
  }
  return `${areaCode} ${'*'.repeat(rest.length - 4)}${rest.slice(-4)}`.trim();
});

// 生日展示：存储值固定为 YYYY-MM-DD，按配置 birthdateDisplayFormat 重组展示
// 支持三种格式：YYYY-MM-DD / DD/MM/YYYY / MM/DD/YYYY
// 年份脱敏：用 **** 替代年份数字
const formattedBirthday = computed(() => {
  const raw = props.customer?.metafield?.value;
  if (!raw) {
    return ''
  };
  const format = props.config?.personInfo?.birthdateDisplayFormat || 'YYYY-MM-DD';
  // 取日期部分（兼容可能带时间的 ISO 字符串），拆出年月日
  const [y, m, d] = String(raw).split('T')[0].split('-');
  if (!y || !m || !d) {
    return raw
  };
  // 年份脱敏：将年份替换为等长的 *
  const maskedYear = '*'.repeat(y.length);
  return format
    .replace(/YYYY/g, maskedYear)
    .replace(/MM/g, m)
    .replace(/DD/g, d);
});

onMounted(() => {
  showLogout.value = isPhonePortrait();
  // 拉取线上区号配置
  loadAreaCodes();
})

watch(() => props.customer, (newVal) => {
  acceptsMarketing.value = newVal.acceptsMarketing;
}, { immediate: true })

watch(() => showAddressForm.value, (newVal) => {
  if (isPhonePortrait()) {
    if (newVal) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = 'auto';
      document.body.style.overflow = 'auto';
    }
  }
});

// 拿取地址数据
const addresses = computed(() => {
  // 从shopify的customer拿取addresses, 并且组装是否默认地址
  const addresses = props.customer.addresses?.edges?.map(item => {
    return {
      ...item.node,
      isDefault: item.node.id === props.customer.defaultAddress.id,
    }
  });
  console.log('addresses',addresses)
  return addresses || [];
})

// 修改acceptsMarketing状态
const handleChange = async () => {
  acceptsMarketing.value = !acceptsMarketing.value;
  loading.value = true;
  const { updateCustomer } = useCustomer();
   if(c4aOAuth2StateNew) {
      await updateCustomer({
        acceptsMarketing: acceptsMarketing.value,
        marketingState: acceptsMarketing.value ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
        emailAddress: props.customer.emailAddress
      })
    } else {
      await updateCustomer({
        acceptsMarketing: acceptsMarketing.value,
      })
    }
  // 更新customer
  emit('update');
  loading.value = false;
}

// 设置默认地址
const clickSetDefault = async (addressId) => {
  try {
    loading.value = true;
    const { setDefaultAddress } = useCustomer();
    await setDefaultAddress(addressId);
    // 更新customer
    emit('update');
    loading.value = false;
  } catch (e) {
    console.error(e);
    loading.value = false;
  }
}

// 删除某个地址
const clickDeleteAddress = async (addressId) => {
  useTipMsgBox({ message: props.config.address.deleteConfirmHint, confirmButtonText: props.config?.confirmButton, cancelButtonText: props.config?.cancelButton }, async () => {
    try {
      loading.value = true;
      const { deleteAddress } = useCustomer();
      await deleteAddress(addressId);
      // 更新customer
      loading.value = false;
      emit('update');
    } catch (e) {
      loading.value = false;
      console.error(e);
    }
  });

}

// 编辑某个地址
const clickEditAddress = (address) => {
  currentAddress.value = address;
  showAddressForm.value = true;
}

// 新增地址
const clickAddAddress = () => {
  // 创建一个空的地址，id为-1，表示新增地址
  currentAddress.value = { id: -1 };
  showAddressForm.value = true;
  const $addressTitle = document.getElementById('address-title');
  console.log('--top', $addressTitle.getBoundingClientRect())
  $addressTitle.scrollIntoView({
    behavior: 'smooth',
    block: 'start' // 可以是 'start', 'center', 'end', 'nearest'
  });
}



const resetCurrentAddress = () => {
  // 重置currentAddress，id为null, 区分不是新增地
  currentAddress.value = { id: null };
}

// 退出登录
const clickLogout = () => {
  useLogOut();
}

const changeLoading = (value) => {
  loading.value = value
}

const showEditBtn = (item) => {
  // b端用户，默认地址才可以编辑
  if (isB2b.value) {
    return item.isDefault ? true : false;
  } else {
    return true;
  }
}

const showDeleteBtn = (item) => {
  if(item.isDefault && c4aOAuth2StateNew) {
    return false
  }
  // b端用户，默认地址不能显示删除按钮
  if (isB2b.value) {
    return item.isDefault ? false : true;
  } else {
    return true;
  }
}

const handleAddressTxt = (item) => {
  const { address1, city, provinceCode, province, zip } = item;
  const provincePart = provinceCode ?? province ?? '';
  return `${address1?.trim()}, ${city}, ${provincePart + (provincePart ? ',' : '')} ${zip}`
}

// 防止滚动穿透
const blockBodyScroll = () => {
  if (isPhonePortrait()) {
    $("body, html").addClass("overflow-hidden");
  }
}

// 解除防止滚动穿透
const unblockBodyScroll = () => {
  if (isPhonePortrait()) {
    $("body, html").removeClass("overflow-hidden");
  }
}
const  handleResetpwdLink=(url, redirectVal)=> {
    // 空值直接返回
    if (!url) {
      return ''
    };

    // 判断是否带域名（http/https 开头）
    const hasDomain = /^https?:\/\//i.test(url);

    // 不带域名 → 不处理，直接返回
    if (!hasDomain) {
      return url;
    }

    try {
      // 带域名：解析 URL
      const urlObj = new URL(url);

      // 判断是否存在 redirect_uri 参数
      if (urlObj.searchParams.has('redirect_uri')) {
        // 存在 → 赋值
        urlObj.searchParams.set('redirect_uri', redirectVal);
      }

      // 返回最终链接
      return urlObj.toString();
    } catch (e) {
      // 解析失败，安全返回原值
      return url;
    }
  }
// 安全跳转：只允许 http/https 协议，防止 javascript: 等危险协议注入
// 支持带域名（https://xxx.com/path）和不带域名（/path）两种格式
const safeRedirect = (url) => {
  if (!url) {
    return
  };
  try {
    // 用 window.location.origin 作为 base，兼容相对路径
    const parsed = new URL(url, window.location.origin);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      console.error('[safeRedirect] Unsafe URL blocked:', url);
      return;
    }
    window.location.href = url;
  } catch (e) {
    console.error('[safeRedirect] Invalid URL:', url);
  }
}

const showPwdFormFn = () =>{
    // C4A情况下
    if(props?.config?.security?.securityType === 'C4A') {
      const windowurl = encodeURIComponent(window.location.href);
      if (isPhonePortrait()) { // 移动端
        const link = props?.config?.security?.resetPWMobileLink
          ? handleResetpwdLink(props?.config?.security?.resetPWMobileLink, windowurl)
          : handleResetpwdLink(props?.config?.security?.resetPWPCLink, windowurl);
        safeRedirect(link);
        return;
      } else {
        safeRedirect(handleResetpwdLink(props?.config?.security?.resetPWPCLink, windowurl));
      }
      return;
    }
    // AEM情况
    showPasswordForm.value = true;
    blockBodyScroll();
  }

/**
 * 观察输入框的变化，更新按钮的disabled属性。
 * 当所有输入框都有值时，按钮才可点击。
 */
// const checkInputVal = () => {
//   nextTick(() => {
//     const $formNode = $('.csr-account-form');
//     // 获取所有require属性的输入框
//     const $requiredInputs = $formNode.find('input[required]');
//     // 获取md-button--primary按钮
//     const $primaryButton = $formNode.find('.csr-account-form__btn-group .md-button--primary');
//     // 判断所有输入框是否都有值
//     const allInputsHaveValue = Array.prototype.every.call($requiredInputs, input => input.value.trim() !== '');
//     // 更新按钮的disabled属性
//     $primaryButton.attr('disabled', !allInputsHaveValue);
    
//     $requiredInputs.on('change', e => {
//       const value = e.target.value;
//       if (!value || value.trim() === '') {
//         $primaryButton.attr('disabled', true);
//       } else {
//         // 判断所有输入框是否都有值
//         const allInputsHaveValue = Array.prototype.every.call($requiredInputs, input => input.value.trim() !== '');
//         // 更新按钮的disabled属性
//         $primaryButton.attr('disabled', !allInputsHaveValue);
//       }
//     });
//   });
// }
</script>

<template>
  <div class="csr-account-my-acount" v-Mdloading="loading">
    <div class="csr-account-my-acount__info csr-account-my-acount__part">
      <div :class='[config.personInfo.subtitle ? "csr-account-my-acount__title csr-account-my-acount__title--sub" : "csr-account-my-acount__title"]'>{{ config.personInfo.title }}</div>
      <div class="csr-account-my-acount__sub-title" v-if="config.personInfo.subtitle">{{ config.personInfo.subtitle }} </div>

      <div class="csr-account-my-acount__item" v-if="!showPersonForm">
        <div class="csr-account-my-acount__content">
          <div>
            <div class="csr-account-my-acount__item__label">{{ config.personInfo.usernameLabel }}</div>
            <div class="csr-account-my-acount__item__value">{{ customer.displayName }}</div>
          </div>
          <div>
            <div class="csr-account-my-acount__item__label">{{ config.personInfo.emailLabel }}</div>
            <div class="csr-account-my-acount__item__value">{{ customer.email }}</div>
          </div>
          <!-- 电话生日 -->
          <div>
            <div class="csr-account-my-acount__item__label">{{ config.personInfo.phoneLabel }}</div>
            <div class="csr-account-my-acount__item__value">{{ customer?.phone ? maskedPhone : '-' }}</div>
          </div>
          <div v-if="customer?.metafield?.value">
            <div class="csr-account-my-acount__item__label">{{ config.personInfo.birthdateLabel }}</div>
            <div class="csr-account-my-acount__item__value">{{ customer?.metafield?.value ? formattedBirthday : '' }}</div>
          </div>
          
        </div>
        <div class="csr-account-my-acount__btn-group">
          <a class="md-button-v2 md-button md-button--black csr-account-my-acount__btn"
            @click="() => { showPersonForm = true; showBirthFrom = false;blockBodyScroll();/* checkInputVal();*/ }">{{ config.personInfo.editButton }}</a>
        </div>
      </div>
      
      <transition name="slide" mode="out-in">
        <PersonForm v-if="showPersonForm" :config="config" :customer="customer"
        @close="() => { showPersonForm = false; emit('update'); unblockBodyScroll(); }" @cancel="() => { showPersonForm = false; unblockBodyScroll(); }"
        @loading="changeLoading" />
      </transition>
      <!-- 添加生日 -->
      <div v-if="!showBirthFrom && !customer?.metafield?.value" :class="showPersonForm ?'csr-account-my-acount__add-address csr-account-my-acount__add-birth showBirthFrom-birth-editor': 'csr-account-my-acount__add-address csr-account-my-acount__add-birth'"  @click="() => { showBirthFrom = true;showPersonForm = false; blockBodyScroll();/* checkInputVal();*/ }">{{ config.personInfo.birthdateLabel }}</div>
      <transition name="slide" mode="out-in">
        <BirthFrom v-if="showBirthFrom" :config="config" :customer="customer"
          @close="() => { showBirthFrom = false; emit('update'); unblockBodyScroll(); }" @cancel="() => { showBirthFrom = false;  unblockBodyScroll(); }"
          @loading="changeLoading" />
      </transition>

      <div class="customer-show-birth-editor" v-if="customer?.metafield?.value && showPersonForm">
            <div class="csr-account-my-acount__item__label">{{ config.personInfo.birthdateLabel }}</div>
            <div class="csr-account-my-acount__item__value">{{ customer?.metafield?.value ? formattedBirthday : '' }}</div>
      </div>
      <MdCheckbox v-if="!isB2b" :value="acceptsMarketing" @change="handleChange"
        class="csr-account-my-acount__checkbox-wrap">
        <span class="csr-account-my-acount__checkbox">
          <span v-html="config.personInfo.notificationDescription"></span>
          <a :href="config.personInfo.privacyPolicyLink" :target="config.target" class="csr-login__policy__txt"
            v-if="config.personInfo.privacyPolicyLink"><span v-html="config.personInfo.privacyPolicyLinkText"></span></a>
        </span>
      </MdCheckbox>
      <div class="csr-account-my-acount__btn-group csr-account-my-acount__btn-group_device">
        <a class="md-button-v2 md-button md-button--black csr-account-my-acount__btn"
          @click="() => { showPersonForm = true; blockBodyScroll();/* checkInputVal();*/ }">{{ config.personInfo.editButton }}</a>
      </div>
      <div v-if="!showBirthFrom  && !customer?.metafield?.value" class="csr-account-my-acount__add-address csr-account-my-acount__add-birth csr-account-my-acount__btn-group_birth_device" @click="() => { showBirthFrom = true;showPersonForm = false; blockBodyScroll();/* checkInputVal();*/ }">
        {{ config.personInfo.birthdateLabel }}
      </div>
 
          

    </div>

    <div class="csr-account-my-acount__address csr-account-my-acount__part">
      <div id="address-title" :class='[config.address?.subtitle ? "csr-account-my-acount__title csr-account-my-acount__title--sub" : "csr-account-my-acount__title"]'>{{ config.address.title }}</div>
      <div class="csr-account-my-acount__sub-title" v-if="config.address.subtitle">{{ config.address.subtitle }}</div>
      <transition name="slide" mode="out-in">
        <AddressForm v-if="showAddressForm && currentAddress.id === -1" :config="config" :customer="customer"
          :edit-address="currentAddress"
          @close="() => { showAddressForm = false; emit('update'); resetCurrentAddress(); unblockBodyScroll(); }"
          @cancel="() => { showAddressForm = false; resetCurrentAddress(); unblockBodyScroll(); }" @loading="changeLoading" />
      </transition>
      <div v-for="(item, index) in addresses" :key="'address' + index"
        :class="[index + 1 === addresses.length && 'csr-account-my-acount__item-last', 'csr-account-my-acount__item_wrap']">
        <div v-if="currentAddress.id !== item.id" class="csr-account-my-acount__item">
          <div class="csr-account-my-acount__content">
            <div>
              <div class="csr-account-my-acount__item__label flex">
                <span>{{ config?.address?.recipientNameLabel }}</span>
                <span v-if="item.isDefault" class="csr-account-my-acount__item__defalut">{{ config?.address?.defaultAddressLabel }}</span>
              </div>
              <div class="csr-account-my-acount__item__value">{{ item.name }}</div>
            </div>
            <div>
              <div class="csr-account-my-acount__item__label">{{ config?.address?.shippingAddressLabel }} </div>
              <div class="csr-account-my-acount__item__value">{{ handleAddressTxt(item) }}</div>
            </div>
          </div>
          <div class="csr-account-my-acount__btn-group">
            <a v-if="!item.isDefault" class="md-button-v2 md-button md-button--black csr-account-my-acount__btn"
              @click="() => { clickSetDefault(item.id); }">{{ config.address.setDefaultButton }}</a>
            <a v-if="showEditBtn(item)" class="md-button-v2 md-button md-button--black csr-account-my-acount__btn"
              @click="() => { clickEditAddress(item); blockBodyScroll();/* checkInputVal();*/ }">{{ config.address.editButton }}</a>
            <a 
             v-if="showDeleteBtn(item)" class="md-button-v2 md-button md-button--black csr-account-my-acount__btn"
              @click="() => { clickDeleteAddress(item.id); }">{{ config.address.deleteButton }}</a>
          </div>
        </div>
        <transition name="slide" mode="out-in">
          <AddressForm v-if="showAddressForm && currentAddress.id === item.id" :config="config" :customer="customer"
            :edit-address="currentAddress"
            @close="() => { showAddressForm = false; emit('update'); resetCurrentAddress(); unblockBodyScroll(); }"
            @cancel="() => { showAddressForm = false; resetCurrentAddress(); unblockBodyScroll(); }" />
        </transition>
      </div>

      <div class="csr-account-my-acount__add-address" @click="clickAddAddress">{{ config.address.addButton }}</div>
    </div>

    <div class="csr-account-my-acount__security">
      <div :class='[config.security?.subtitle ? "csr-account-my-acount__title csr-account-my-acount__title--sub" : "csr-account-my-acount__title"]'>{{ config.security.title }}</div>
      <div class="csr-account-my-acount__sub-title" v-if="config.security.subtitle">{{ config.security.subtitle }}</div>
      <div class="csr-account-my-acount__item" v-if="!showPasswordForm">
        <div class="csr-account-my-acount__content">
          <div>
            <div class="csr-account-my-acount__item__label">{{ config.security.passwordLabel }}</div>
            <div class="csr-account-my-acount__item__value">•••••••</div>
          </div>
        </div>
        <div v-if="!isB2b" class="csr-account-my-acount__btn-group">
          <a class="md-button-v2 md-button md-button--black csr-account-my-acount__btn"
            @click="showPwdFormFn"
          >{{ config.security.editButton }}</a>
        </div>
      </div>
      <transition name="slide" mode="out-in">
        <PasswordForm v-if="showPasswordForm" :config="config" :customer="customer"
          @close="() => { showPasswordForm = false; unblockBodyScroll(); }" @cancel="() => { showPasswordForm = false; unblockBodyScroll(); }"
          @loading="changeLoading" />
      </transition>
    </div>

    <div class="csr-account-my-acount__logout gws-mb-sticky-to-bottom" v-if="showLogout">
      <div class="csr-account-my-acount__logout__wrapper">
        <a class="csr-account-my-acount__logout-btn md-button-v2 md-button md-button--black"
          @click="() => clickLogout()">{{ config.logout }}</a>
      </div>
    </div>
  </div>
</template>